import { z } from "zod";

/**
 * One schema per concept, used for three jobs: parsing an API response, typing a
 * form, and validating a route's search params. Declaring it once is what keeps
 * those three from drifting -- the failure this whole package exists to prevent.
 *
 * Every shape mirrors a record in the backend's `View` class. If a field is added
 * there it is added here, and TypeScript then points at every screen that needs
 * to change.
 */

export const accountSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  status: z.enum(["PENDING_VERIFICATION", "ACTIVE", "SUSPENDED", "CLOSED"]),
  lastLoginAt: z.string().nullable(),
});

export const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  legalName: z.string().nullable(),
  country: z.string().nullable(),
  billingEmail: z.string().nullable(),
  status: z.enum(["ACTIVE", "SUSPENDED", "CLOSED"]),
  createdAt: z.string(),
});

export const organizationRoleSchema = z.enum(["OWNER", "ADMIN", "BILLING", "MEMBER"]);

export const memberSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  email: z.string(),
  name: z.string(),
  role: organizationRoleSchema,
});

export const productSchema = z.object({
  code: z.string(),
  name: z.string(),
  tagline: z.string().nullable(),
  icon: z.string().nullable(),
  accent: z.string().nullable(),
  appUrl: z.string().nullable(),
  available: z.boolean(),
  owned: z.boolean(),
});

export const planSchema = z.object({
  id: z.string(),
  productCode: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  entitlements: z.record(z.string(), z.string()),
});

export const subscriptionSchema = z.object({
  id: z.string(),
  productCode: z.string(),
  planCode: z.string().nullable(),
  planName: z.string().nullable(),
  status: z.enum(["TRIAL", "ACTIVE", "PAST_DUE", "CANCELLED"]),
  startedAt: z.string(),
  endsAt: z.string().nullable(),
});

export const workspaceStatusSchema = z.enum([
  "DRAFT",
  "PROVISIONING",
  "ACTIVE",
  "SUSPENDED",
  "FAILED",
  "ARCHIVED",
]);

export const workspaceSchema = z.object({
  id: z.string(),
  productCode: z.string(),
  code: z.string(),
  name: z.string(),
  status: workspaceStatusSchema,
  primaryHost: z.string().nullable(),
  externalRef: z.string().nullable(),
  provisioningError: z.string().nullable(),
  entitlements: z.record(z.string(), z.string()),
  createdAt: z.string(),
});

export const provisioningKindSchema = z.enum([
  "WORKSPACE",
  "COMPANY",
  "MARKETPLACE",
  "DOMAIN",
  "QUOTA",
]);

export const provisioningStatusSchema = z.enum([
  "PENDING",
  "APPROVED",
  "REJECTED",
  "COMPLETED",
  "FAILED",
]);

export const provisioningRequestSchema = z.object({
  id: z.string(),
  kind: provisioningKindSchema,
  status: provisioningStatusSchema,
  payload: z.string().nullable(),
  note: z.string().nullable(),
  organizationId: z.string(),
  organizationName: z.string(),
  requestedBy: z.string().nullable(),
  decidedBy: z.string().nullable(),
  decisionNote: z.string().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
});

export const auditEventSchema = z.object({
  id: z.string(),
  actorType: z.enum(["ACCOUNT", "STAFF", "SYSTEM"]),
  actorLabel: z.string().nullable(),
  action: z.string(),
  resourceType: z.string().nullable(),
  resourceId: z.string().nullable(),
  summary: z.string().nullable(),
  requestId: z.string().nullable(),
  createdAt: z.string(),
});

export const supportNoteSchema = z.object({
  id: z.string(),
  author: z.string(),
  body: z.string(),
  pinned: z.boolean(),
  createdAt: z.string(),
});

export const sessionSchema = z.object({
  id: z.string(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
});

export const meSchema = z.object({
  account: accountSchema,
  organization: organizationSchema,
  role: organizationRoleSchema,
  products: z.array(z.string()),
});

export const tokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  tokenType: z.string(),
});

export const workspaceTokenSchema = z.object({
  token: z.string(),
  productCode: z.string(),
  appUrl: z.string().nullable(),
  expiresIn: z.number(),
});

export const productIncidentSchema = z.object({
  source: z.string().nullable(),
  code: z.string().nullable(),
  message: z.string().nullable(),
  severity: z.string().nullable(),
  requestId: z.string().nullable(),
  firstSeen: z.string().nullable(),
  lastSeen: z.string().nullable(),
  count: z.number(),
  status: z.string().nullable(),
});

export const productHealthSchema = z.object({
  reporting: z.boolean(),
  message: z.string().nullable(),
  readiness: z.array(
    z.object({
      feature: z.string(),
      status: z.string(),
      missing: z.array(z.string()),
    }),
  ),
  integrations: z.array(
    z.object({ name: z.string(), status: z.string(), detail: z.string().nullable() }),
  ),
  queues: z.record(z.string(), z.number()),
});

export const searchHitSchema = z.object({
  organizationId: z.string(),
  name: z.string(),
  billingEmail: z.string().nullable(),
  status: z.string(),
  workspaceCount: z.number(),
});

export const organizationDetailSchema = z.object({
  organization: organizationSchema,
  members: z.array(memberSchema),
  subscriptions: z.array(subscriptionSchema),
  workspaces: z.array(workspaceSchema),
  requests: z.array(provisioningRequestSchema),
});

export const workspaceHealthSchema = z.object({
  workspace: workspaceSchema,
  health: productHealthSchema,
});

export const workspaceIncidentsSchema = z.object({
  workspaceId: z.string(),
  workspaceName: z.string(),
  productCode: z.string(),
  incidents: z.array(productIncidentSchema),
});

export const customerIncidentsSchema = z.object({
  organizationId: z.string(),
  organizationName: z.string(),
  workspaceId: z.string(),
  workspaceName: z.string(),
  productCode: z.string(),
  incidents: z.array(productIncidentSchema),
});

export const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  fields: z.array(z.object({ field: z.string(), message: z.string() })).default([]),
  requestId: z.string().nullable(),
  timestamp: z.string().nullable(),
});

export const domainStatusSchema = z.enum(["PENDING_VERIFICATION", "VERIFIED", "LIVE", "FAILED"]);

/**
 * `verificationToken` is shown to the customer on purpose -- it is what they paste into their own
 * DNS, so it is an instruction rather than a secret. Nothing else about a domain is sensitive.
 */
export const domainSchema = z.object({
  id: z.string(),
  hostname: z.string(),
  kind: z.enum(["ADMIN", "STOREFRONT"]),
  /** The marketplace a storefront host serves; empty for an admin host. */
  targetRef: z.string(),
  status: domainStatusSchema,
  primaryHost: z.boolean(),
  verificationToken: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  certStatus: z.string().nullable(),
  workspaceId: z.string(),
  workspaceCode: z.string(),
  productCode: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  createdAt: z.string(),
});

export const domainTargetSchema = z.object({ code: z.string(), name: z.string() });

export const provisionedRefSchema = z.object({
  ref: z.string(),
  code: z.string(),
  name: z.string(),
});

export function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    page: z.number(),
    size: z.number(),
    total: z.number(),
    totalPages: z.number(),
  });
}

export type Account = z.infer<typeof accountSchema>;
export type Organization = z.infer<typeof organizationSchema>;
export type OrganizationRole = z.infer<typeof organizationRoleSchema>;
export type Member = z.infer<typeof memberSchema>;
export type Product = z.infer<typeof productSchema>;
export type Plan = z.infer<typeof planSchema>;
export type Subscription = z.infer<typeof subscriptionSchema>;
export type Workspace = z.infer<typeof workspaceSchema>;
export type WorkspaceStatus = z.infer<typeof workspaceStatusSchema>;
export type ProvisioningKind = z.infer<typeof provisioningKindSchema>;
export type ProvisioningStatus = z.infer<typeof provisioningStatusSchema>;
export type ProvisioningRequest = z.infer<typeof provisioningRequestSchema>;
export type AuditEvent = z.infer<typeof auditEventSchema>;
export type SupportNote = z.infer<typeof supportNoteSchema>;
export type Session = z.infer<typeof sessionSchema>;
export type CustomerIncidents = z.infer<typeof customerIncidentsSchema>;
export type Me = z.infer<typeof meSchema>;
export type Tokens = z.infer<typeof tokensSchema>;
export type WorkspaceToken = z.infer<typeof workspaceTokenSchema>;
export type ProductIncident = z.infer<typeof productIncidentSchema>;
export type ProductHealth = z.infer<typeof productHealthSchema>;
export type SearchHit = z.infer<typeof searchHitSchema>;
export type OrganizationDetail = z.infer<typeof organizationDetailSchema>;
export type WorkspaceHealth = z.infer<typeof workspaceHealthSchema>;
export type WorkspaceIncidents = z.infer<typeof workspaceIncidentsSchema>;
export type Domain = z.infer<typeof domainSchema>;
export type DomainStatus = z.infer<typeof domainStatusSchema>;
export type DomainTarget = z.infer<typeof domainTargetSchema>;
export type ProvisionedRef = z.infer<typeof provisionedRefSchema>;
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;
